import React, { useState, useEffect } from 'react';
import { X, Search, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { getImageSuggestions } from '../../services/imageService';
import { useTranslation } from 'react-i18next';

/**
 * ImageSuggestionModal - Modal for selecting AI-suggested images
 * 
 * Features:
 * - Grid display of suggested images
 * - Hover preview with zoom effect
 * - Tags display
 * - Category filter
 * - Loading and error states
 * 
 * @param {string} category - Image category (services, projects, etc.)
 * @param {boolean} isOpen - Modal open state
 * @param {function} onClose - Close callback
 * @param {function} onSelect - Image selection callback (receives URL)
 */
const ImageSuggestionModal = ({ category = 'services', isOpen, onClose, onSelect }) => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(category);
  const [hoveredImage, setHoveredImage] = useState(null);

  const categories = [
    { value: 'hero', label: 'Hero' },
    { value: 'about', label: 'About' },
    { value: 'services', label: 'Services' },
    { value: 'projects', label: 'Projects' },
    { value: 'insights', label: 'Insights' },
    { value: 'highlights', label: 'Highlights' },
    { value: 'clients', label: 'Clients' },
    { value: 'stats', label: 'Stats' },
  ];

  useEffect(() => {
    if (isOpen) {
      fetchSuggestions(selectedCategory);
    }
  }, [isOpen, selectedCategory]);

  const fetchSuggestions = async (cat) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await getImageSuggestions(cat);
      setImages(result.images || []);
    } catch (err) {
      setError(err.detail || 'Failed to load suggestions');
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (imageUrl) => {
    if (onSelect) {
      onSelect(imageUrl);
    }
    onClose();
  };

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ImageIcon className="w-6 h-6 text-[#00B4D8]" />
              Sugestões de Imagem
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Selecione uma imagem profissional para o seu conteúdo
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Category Selector */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Categoria
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleCategoryChange(cat.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedCategory === cat.value
                    ? 'bg-[#00B4D8] text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin mx-auto mb-3" />
                <p className="text-gray-500">A carregar sugestões...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-600">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchSuggestions(selectedCategory)}
                className="mt-3"
              >
                Tentar Novamente
              </Button>
            </div>
          )}

          {!loading && !error && images.length === 0 && (
            <div className="text-center py-12">
              <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nenhuma sugestão disponível</p>
            </div>
          )}

          {!loading && !error && images.length > 0 && (
            <div className="grid grid-cols-3 gap-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="group relative bg-gray-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105"
                  onClick={() => handleSelect(image.url)}
                  onMouseEnter={() => setHoveredImage(image.id)}
                  onMouseLeave={() => setHoveredImage(null)}
                >
                  {/* Image */}
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.tags?.join(', ') || 'Suggested image'}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    />
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <Button
                        size="sm"
                        className="bg-[#00B4D8] hover:bg-[#48CAE4] text-white w-full"
                      >
                        Usar esta imagem
                      </Button>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {image.tags?.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {images.length} {images.length === 1 ? 'imagem' : 'imagens'} disponíveis
          </p>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageSuggestionModal;
